// File: apps/web/src/analysis/premium/sales-charts.ts

import { createChart } from '@/analysis/helpers'
import { chartTooltip, currencyTooltipCallback, mergeChartOptions, shortenCurrency } from '@/analysis/utils/chart-formatters'
import type { SalesSummary } from '@/analysis/sections/general/sales/types'
import { formatCurrency, formatNumber } from '@/utils/string'
// --- START JOB 14 FIXES (IMPORTS) ---
import { AlsoStoreFn, createMaybeAlsoStoreFn, maybeAlsoStore } from '@/analysis/utils/store-helpers'
import { formatNumber as formatNumberUtil, formatCurrency as formatCurrencyUtil, formatDecimalBasedPercentage } from '@/utils/string'
import { deepmerge } from 'deepmerge-ts'
// --- END JOB 14 FIXES (IMPORTS) ---
import * as $store from '@/store'
/**
 * Generates a yearly omzet chart, aggregating data by month.
 */
export function generateOmzetYearlyChart(summaries: SalesSummary[], canvasId: string) {
  const monthlyTotals = Array(12).fill(0)
  summaries.forEach((s) => {
    const month = s.date.getMonth() // 0-11
    monthlyTotals[month] += s.totalOmzet
  })

  const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  createChart(canvasId, 'bar', {
    labels,
    datasets: [{
      label: 'Total Omzet',
      data: monthlyTotals,
      backgroundColor: '#4f46e5',
    }],
  }, mergeChartOptions(
    { scales: { y: { ticks: { callback: (n: any) => shortenCurrency(n) } } } },
    chartTooltip({ label: currencyTooltipCallback }),
  ))
}

/**
 * Generates a quarterly omzet chart, breaking down data by month within that quarter.
 */
export function generateOmzetQuarterlyChart(summaries: SalesSummary[], canvasId: string) {
  if (summaries.length === 0) {
    // Clear the chart if there's no data
    createChart(canvasId, 'bar', { labels: [], datasets: [] })
    return
  }

  // Determine the quarter from the first data point
  const firstDate = summaries[0].date
  const year = firstDate.getFullYear()
  const quarter = Math.floor(firstDate.getMonth() / 3) // 0 for Q1, 1 for Q2, etc.
  const startMonth = quarter * 3 // The starting month index (0 for Jan, 3 for Apr, etc.)

  const monthlyTotals = [0, 0, 0]
  const monthLabels = [
    new Date(year, startMonth).toLocaleString('default', { month: 'long' }),
    new Date(year, startMonth + 1).toLocaleString('default', { month: 'long' }),
    new Date(year, startMonth + 2).toLocaleString('default', { month: 'long' }),
  ]

  // Aggregate sales into the correct month bucket
  summaries.forEach((s) => {
    const monthInQuarterIndex = s.date.getMonth() - startMonth // Will be 0, 1, or 2
    if (monthInQuarterIndex >= 0 && monthInQuarterIndex < 3) {
      monthlyTotals[monthInQuarterIndex] += s.totalOmzet
    }
  })

  createChart(canvasId, 'bar', {
    labels: monthLabels,
    datasets: [{
      label: 'Total Omzet',
      data: monthlyTotals,
      backgroundColor: '#4f46e5',
    }],
  }, mergeChartOptions(
    { scales: { y: { ticks: { callback: (n: any) => shortenCurrency(n) } } } },
    chartTooltip({ label: currencyTooltipCallback }),
  ))
}

/**
 * Generates a weekly omzet chart, showing data for each day of the week.
 */
export function generateOmzetWeeklyChart(summaries: SalesSummary[], canvasId: string) {
  const dailyTotals = Array(7).fill(0) // 0=Sunday, 1=Monday, ...
  summaries.forEach((s) => {
    const day = s.date.getDay()
    dailyTotals[day] += s.totalOmzet
  })

  // Reorder to start from Monday
  const orderedTotals = [...dailyTotals.slice(1), dailyTotals[0]]
  const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  createChart(canvasId, 'line', {
    labels,
    datasets: [{
      label: 'Total Omzet',
      data: orderedTotals,
      borderColor: '#4f46e5',
      tension: 0.1,
    }],
  }, mergeChartOptions(
    { scales: { y: { beginAtZero: true, ticks: { callback: (n: any) => shortenCurrency(n) } } } },
    chartTooltip({ label: currencyTooltipCallback }),
  ))
}

// --- START MODIFICATION (JOB_14) ---

/**
 * Populates the two rows of KPI cards for Period A and Period B.
 */
export function populateTimeComparisonKPIs(
  periodAData: SalesSummary[], 
  periodBData: SalesSummary[], 
  config?: { alsoStore?: AlsoStoreFn } // <-- ADDED CONFIG
) {
  const calculateTotals = (summaries: SalesSummary[]) => {
    const totals = summaries.reduce((acc, s) => {
      acc.omzet += s.totalOmzet
      acc.checks += s.totalTransactions
      return acc
    }, { omzet: 0, checks: 0, avgCheck: 0 });
    (totals as any).avgCheck = totals.checks > 0 ? totals.omzet / totals.checks : 0
    return totals
  }

  const totalsA = calculateTotals(periodAData)
  const totalsB = calculateTotals(periodBData)

  const alsoStore = createMaybeAlsoStoreFn(config?.alsoStore)
  alsoStore(totalsA.omzet, (v) => ({ kpis: { periodA: { totalOmzet: formatCurrencyUtil(v) } } }))
  alsoStore(totalsA.checks, (v) => ({ kpis: { periodA: { totalCheck: formatNumberUtil(v) } } }))
  alsoStore(totalsA.avgCheck, (v) => ({ kpis: { periodA: { avgPerCheck: formatCurrencyUtil(v) } } }))
  alsoStore(totalsB.omzet, (v) => ({ kpis: { periodB: { totalOmzet: formatCurrencyUtil(v) } } }))
  alsoStore(totalsB.checks, (v) => ({ kpis: { periodB: { totalCheck: formatNumberUtil(v) } } }))
  alsoStore(totalsB.avgCheck, (v) => ({ kpis: { periodB: { avgPerCheck: formatCurrencyUtil(v) } } }))

  // Calculate and store growth
  const omzetGrowth = totalsA.omzet > 0 ? (totalsB.omzet - totalsA.omzet) / totalsA.omzet : (totalsB.omzet > 0 ? 1 : 0)
  const checkGrowth = totalsA.checks > 0 ? (totalsB.checks - totalsA.checks) / totalsA.checks : (totalsB.checks > 0 ? 1 : 0)
  const avgCheckGrowth = totalsA.avgCheck > 0 ? (totalsB.avgCheck - totalsA.avgCheck) / totalsA.avgCheck : (totalsB.avgCheck > 0 ? 1 : 0)
  
  alsoStore(omzetGrowth, (v) => ({ kpis: { growth: { totalOmzet: formatDecimalBasedPercentage(v, 1) } } }))
  alsoStore(checkGrowth, (v) => ({ kpis: { growth: { totalCheck: formatDecimalBasedPercentage(v, 1) } } }))
  alsoStore(avgCheckGrowth, (v) => ({ kpis: { growth: { avgPerCheck: formatDecimalBasedPercentage(v, 1) } } }))


  const updateDom = (periodSuffix: 'a' | 'b', data: typeof totalsA) => {
    const omzetEl = document.getElementById(`premium-time-sales-total-omzet-${periodSuffix}`)
    const checkEl = document.getElementById(`premium-time-sales-total-check-${periodSuffix}`)
    const avgCheckEl = document.getElementById(`premium-time-sales-avg-check-${periodSuffix}`)
    if (omzetEl) omzetEl.textContent = formatCurrency(data.omzet)
    if (checkEl) checkEl.textContent = formatNumber(data.checks)
    if (avgCheckEl) avgCheckEl.textContent = formatCurrency(data.avgCheck)
  }

  updateDom('a', totalsA)
  updateDom('b', totalsB)
}

/**
 * Generates a flexible time-series comparison chart for Omzet, TC, or APC.
 */
export function generatePremiumTimeComparisonChart(
  periodAData: SalesSummary[],
  periodBData: SalesSummary[],
  range: string,
  canvasId: string,
  metric: 'totalOmzet' | 'totalTransactions' | 'apc',
  config?: { alsoStore?: AlsoStoreFn } // <-- ADDED CONFIG
) {
  const alsoStore = createMaybeAlsoStoreFn(config?.alsoStore)
  const aiData: any = { periodA: {}, periodB: {} }

  if (range === 'quarterly') {
    const aggregateMonthly = (summaries: SalesSummary[]) => {
      const monthlyTotals = [0, 0, 0]
      if (summaries.length === 0) return monthlyTotals

      const startMonth = Math.floor(summaries[0].date.getMonth() / 3) * 3 // 0 for Q1, 3 for Q2, etc.
      summaries.forEach((s) => {
        const monthInQuarterIndex = s.date.getMonth() - startMonth
        if (monthInQuarterIndex >= 0 && monthInQuarterIndex < 3) {
          if (metric === 'apc') {
            // For APC, we need to re-calculate, not just sum. This is a simplification.
            // A more accurate way would be to sum total omzet and total transactions for the month.
            monthlyTotals[monthInQuarterIndex] += s.totalOmzet / s.totalTransactions || 0
          } else {
            monthlyTotals[monthInQuarterIndex] += s[metric] || 0
          }
        }
      })
      return monthlyTotals
    }

    const totalsA = aggregateMonthly(periodAData)
    const totalsB = aggregateMonthly(periodBData)

    const labels = ['First Month of Quarter', 'Second Month', 'Third Month']
    
    // --- ADDED AI STORE LOGIC ---
    const formatValue = metric === 'totalTransactions' ? formatNumberUtil : formatCurrencyUtil
    labels.forEach((label, i) => {
      aiData.periodA[label] = formatValue(totalsA[i])
      aiData.periodB[label] = formatValue(totalsB[i])
    })
    alsoStore(aiData, (v) => ({ [`${metric}_comparison`]: v }))
    // --- END ADDED AI STORE LOGIC ---

    createChart(canvasId, 'bar', {
      labels,
      datasets: [
        { label: `Period A`, data: totalsA, backgroundColor: '#A5B4FC' },
        { label: `Period B`, data: totalsB, backgroundColor: '#4F46E5' },
      ],
    }, mergeChartOptions(
      { scales: { y: { beginAtZero: true, ticks: { callback: (n: any) => metric === 'totalOmzet' || metric === 'apc' ? shortenCurrency(n) : n } } } },
      chartTooltip({ label: (ctx: any) => `${ctx.dataset.label}: ${metric === 'totalOmzet' || metric === 'apc' ? formatCurrency(ctx.parsed.y) : formatNumber(ctx.parsed.y)}` }),
    ))
  } else {
    let labels: string[] = []
    const mapA = new Map<string, { value: number, count: number, omzet: number, checks: number }>()
    const mapB = new Map<string, { value: number, count: number, omzet: number, checks: number }>()

    // Helper to get the aggregation key and label based on range
    const getKeyAndLabel = (d: Date, r: string) => {
      let key = ''
      let label = ''
      if (r === 'yearly') { // Compare months within the year
        key = String(d.getMonth())
        label = d.toLocaleString('id-ID', { month: 'short' })
      } else if (r === 'quarterly' || r === 'monthly') { // Compare days within the month/quarter
        key = String(d.getDate())
        label = String(d.getDate())
      } else if (r === 'weekly') { // Compare days of the week
        const dayOfWeek = d.getDay() // 0 = Sunday, 1 = Monday, ...
        key = String(dayOfWeek)
        label = d.toLocaleString('id-ID', { weekday: 'short' })
      }
      return { key, label }
    }

    // Aggregate data for a given period
    const aggregatePeriodData = (summaries: SalesSummary[], aggregationMap: Map<string, { value: number, count: number, omzet: number, checks: number }>) => { 
      summaries.forEach((s) => {
        const { key } = getKeyAndLabel(s.date, range)
        if (key) {
          const current = aggregationMap.get(key) || { value: 0, count: 0, omzet: 0, checks: 0 } 
          
          // Store raw omzet/checks for APC calculation
          current.omzet += s.totalOmzet || 0
          current.checks += s.totalTransactions || 0
          
          if (metric === 'apc') {
            // We will calculate APC at the end
          } else {
            current.value += s[metric] || 0
          }
          current.count += 1 // Count of days
          
          aggregationMap.set(key, current)
        }
      })
    }

    // Populate aggregation maps
    aggregatePeriodData(periodAData, mapA)
    aggregatePeriodData(periodBData, mapB)

    // Determine common labels and extract data
    const allKeys = new Set<string>()
    periodAData.forEach((s) => allKeys.add(getKeyAndLabel(s.date, range).key))
    periodBData.forEach((s) => allKeys.add(getKeyAndLabel(s.date, range).key))

    const sortedKeys = Array.from(allKeys).sort((a, b) => parseInt(a) - parseInt(b))

    labels = sortedKeys.map((key) => getKeyAndLabel(new Date(2000, parseInt(key), parseInt(key) + 1), range).label) // Dummy date for label formatting
    if (range === 'quarterly' || range === 'monthly') labels = sortedKeys // For day-of-month, labels are just the day number
    if (range === 'yearly') labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    if (range === 'weekly') labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

    const dataA = sortedKeys.map((key) => {
      const aggregated = mapA.get(key)
      if (!aggregated) return null
      // --- MODIFIED APC CALCULATION ---
      if (metric === 'apc') {
        return aggregated.checks > 0 ? aggregated.omzet / aggregated.checks : 0
      }
      return aggregated.value
      // --- END MODIFIED ---
    })

    const dataB = sortedKeys.map((key) => {
      const aggregated = mapB.get(key)
      if (!aggregated) return null
      // --- MODIFIED APC CALCULATION ---
      if (metric === 'apc') {
        return aggregated.checks > 0 ? aggregated.omzet / aggregated.checks : 0
      }
      return aggregated.value
      // --- END MODIFIED ---
    })

    // Ensure labels array is correctly aligned for the range
    if (range === 'yearly') {
      labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    } else if (range === 'weekly') {
      labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      const reorderedDataA = new Array(7).fill(null)
      const reorderedDataB = new Array(7).fill(null)
      sortedKeys.forEach((key, index) => {
        reorderedDataA[parseInt(key)] = dataA[index]
        reorderedDataB[parseInt(key)] = dataB[index]
      })
      dataA.splice(0, dataA.length, ...reorderedDataA)
      dataB.splice(0, dataB.length, ...reorderedDataB)
    } else if (range === 'monthly' || range === 'quarterly') {
      const maxDay = Math.max(...sortedKeys.map((k) => parseInt(k)))
      labels = Array.from({ length: maxDay }, (_, i) => String(i + 1))

      const fullMonthDataA = new Array(maxDay).fill(null)
      const fullMonthDataB = new Array(maxDay).fill(null)
      sortedKeys.forEach((key, index) => {
        fullMonthDataA[parseInt(key) - 1] = dataA[index]
        fullMonthDataB[parseInt(key) - 1] = dataB[index]
      })
      dataA.splice(0, dataA.length, ...fullMonthDataA)
      dataB.splice(0, dataB.length, ...fullMonthDataB)
    }

    // --- ADDED AI STORE LOGIC ---
    const formatValue = metric === 'totalTransactions' ? formatNumberUtil : formatCurrencyUtil
    labels.forEach((label, i) => {
      if (dataA[i] !== null) aiData.periodA[label] = formatValue(dataA[i] as number)
      if (dataB[i] !== null) aiData.periodB[label] = formatValue(dataB[i] as number)
    })
    alsoStore(aiData, (v) => ({ [`${metric}_comparison_trend`]: v }))
    // --- END ADDED AI STORE LOGIC ---


    createChart(canvasId, 'line', {
      labels,
      datasets: [
        { label: `Period A`, data: dataA, borderColor: '#A5B4FC', tension: 0.1, spanGaps: true, pointRadius: 3 },
        { label: `Period B`, data: dataB, borderColor: '#4F46E5', tension: 0.1, spanGaps: true, pointRadius: 3 },
      ],
    }, mergeChartOptions(
      { scales: { y: { beginAtZero: true, ticks: { callback: (n: any) => metric === 'totalOmzet' || metric === 'apc' ? shortenCurrency(n) : n } } } },
      chartTooltip({ label: (ctx: any) => `${ctx.dataset.label}: ${metric === 'totalOmzet' || metric === 'apc' ? formatCurrency(ctx.parsed.y) : formatNumber(ctx.parsed.y)}` }),
    ))
  }
}

// In: apps/web/src/analysis/premium/sales-charts.ts

export function generatePremiumMenuTrendComparisonChart(
  periodAData: SalesSummary[],
  periodBData: SalesSummary[],
  range: string,
  canvasId: string,
  selectedMenus: string[],
  config?: { alsoStore?: AlsoStoreFn } // <-- ADDED CONFIG
) {
  if (selectedMenus.length === 0) {
    createChart(canvasId, 'line', { labels: [], datasets: [] })
    return
  }

  const colors = ['#3B82F6', '#10B981', '#F97316', '#8B5CF6', '#EF4444']
  let labels: string[] = []
  const datasets: any[] = []

  const alsoStore = createMaybeAlsoStoreFn(config?.alsoStore)
  const aiData: any = { periodA: {}, periodB: {} }

  // --- START MODIFICATION (JOB 18 FIX) ---
  const hiddenMenus = $store.getConfigValue('hiddenMenus') || []
  const hiddenCategories = $store.getConfigValue('hiddenCategories') || []
  // --- END MODIFICATION ---

  const aggregateDataForMenu = (summaries: SalesSummary[], menuName: string) => {
    // Check if the menu itself is hidden
    if (hiddenMenus.includes(menuName)) return new Map<string, number>()

    const map = new Map<string, number>()
    summaries.forEach((s) => {
      let key = ''
      const d = s.date
      if (range === 'yearly') key = String(d.getMonth())
      else if (range === 'monthly' || range === 'quarterly') key = String(d.getDate())
      else if (range === 'weekly') key = String(d.getDay())

      if (key) {
        let quantity = 0
        if (s.menuItemQuantities) {
          for (const category in s.menuItemQuantities) {
            // Check for hidden category
            if (hiddenCategories.includes(category)) continue
            
            if (s.menuItemQuantities[category][menuName]) {
              quantity += s.menuItemQuantities[category][menuName]
            }
          }
        }
        if (quantity > 0) {
          map.set(key, (map.get(key) || 0) + quantity)
        }
      }
    })
    return map
  }

  if (range === 'yearly') {
    labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  } else if (range === 'weekly') {
    labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  } else { // monthly or quarterly (daily axis)
    const allDays = new Set<string>()
    periodAData.forEach((s) => allDays.add(String(s.date.getDate())))
    periodBData.forEach((s) => allDays.add(String(s.date.getDate())))
    const sortedDays = Array.from(allDays).sort((a, b) => parseInt(a) - parseInt(b))
    const maxDay = parseInt(sortedDays[sortedDays.length - 1] || '0')
    labels = Array.from({ length: maxDay }, (_, i) => String(i + 1))
  }

  selectedMenus.forEach((menuName, index) => {
    const color = colors[index % colors.length]
    const mapA = aggregateDataForMenu(periodAData, menuName)
    const mapB = aggregateDataForMenu(periodBData, menuName)

    // Helper to format data for chart and AI
    const getData = (map: Map<string, number>) => {
      let keysToMap = labels.map((_, i) => String(i)); // For yearly/weekly
      if (range === 'monthly' || range === 'quarterly') {
        keysToMap = labels.map((l) => l); // For daily (1, 2, 3...)
      } else if (range === 'weekly') {
        keysToMap = ['0', '1', '2', '3', '4', '5', '6']; // Sun-Sat
      }
      
      const chartData = keysToMap.map((key) => map.get(key) || null)
      
      // Format for AI
      const aiChartData: Record<string, string> = {}
      labels.forEach((label, i) => {
        if (chartData[i] !== null) {
          aiChartData[label] = formatNumberUtil(chartData[i] as number)
        }
      })
      
      return { chartData, aiChartData }
    }

    const { chartData: dataA, aiChartData: aiDataA } = getData(mapA)
    const { chartData: dataB, aiChartData: aiDataB } = getData(mapB)

    aiData.periodA[menuName] = aiDataA
    aiData.periodB[menuName] = aiDataB

    datasets.push({ label: `${menuName} (A)`, data: dataA, borderColor: color, borderDash: [5, 5], tension: 0.1, spanGaps: true })
    datasets.push({ label: `${menuName} (B)`, data: dataB, borderColor: color, tension: 0.1, spanGaps: true })
  })

  alsoStore(aiData, (v) => ({ menuTrendComparison: v }))

  createChart(canvasId, 'line', { labels, datasets }, mergeChartOptions(
    { plugins: { tooltip: { mode: 'index', intersect: false } } },
    chartTooltip({ label: (ctx: any) => `${ctx.dataset.label}: ${formatNumber(ctx.parsed.y)} items` }),
  ))
}

/**
 * Populates the two rows of KPI cards for Branch A and Branch B.
 */
export function populateBranchComparisonKPIs(
  periodData: SalesSummary[], 
  branchA: string, 
  branchB: string,
  config?: { alsoStore?: AlsoStoreFn } // <--- ADDED
) {
  const calculateTotals = (summaries: SalesSummary[], branchName: string) => {
    const branchSummaries = summaries.filter((s) => s.branches.includes(branchName))
    const totals = branchSummaries.reduce((acc, s) => {
      acc.omzet += s.totalOmzet
      acc.checks += s.totalTransactions
      return acc
    }, { omzet: 0, checks: 0 });
    (totals as any).avgCheck = totals.checks > 0 ? totals.omzet / totals.checks : 0
    return totals
  }

  const totalsA = calculateTotals(periodData, branchA)
  const totalsB = calculateTotals(periodData, branchB)

  // --- [NEW] AI Data Storage ---
  const alsoStore = createMaybeAlsoStoreFn(config?.alsoStore)
  
  // Store KPIs for Branch A
  alsoStore(totalsA.omzet, (v) => ({ kpis: { [branchA]: { totalOmzet: formatCurrencyUtil(v) } } }))
  alsoStore(totalsA.checks, (v) => ({ kpis: { [branchA]: { totalCheck: formatNumberUtil(v) } } }))
  alsoStore(totalsA.avgCheck, (v) => ({ kpis: { [branchA]: { avgPerCheck: formatCurrencyUtil(v) } } }))
  
  // Store KPIs for Branch B
  alsoStore(totalsB.omzet, (v) => ({ kpis: { [branchB]: { totalOmzet: formatCurrencyUtil(v) } } }))
  alsoStore(totalsB.checks, (v) => ({ kpis: { [branchB]: { totalCheck: formatNumberUtil(v) } } }))
  alsoStore(totalsB.avgCheck, (v) => ({ kpis: { [branchB]: { avgPerCheck: formatCurrencyUtil(v) } } }))
  // --- [END NEW] ---

  const updateDom = (periodSuffix: 'a' | 'b', data: typeof totalsA) => {
    const omzetEl = document.getElementById(`premium-branch-sales-total-omzet-${periodSuffix}`)
    const checkEl = document.getElementById(`premium-branch-sales-total-check-${periodSuffix}`)
    const avgCheckEl = document.getElementById(`premium-branch-sales-avg-check-${periodSuffix}`)
    if (omzetEl) omzetEl.textContent = formatCurrency(data.omzet)
    if (checkEl) checkEl.textContent = formatNumber(data.checks)
    if (avgCheckEl) avgCheckEl.textContent = formatCurrency((data as any).avgCheck)
  }

  updateDom('a', totalsA)
  updateDom('b', totalsB)

  const labelA = document.getElementById('premium-branch-sales-branch-a-label')
  const labelB = document.getElementById('premium-branch-sales-branch-b-label')
  if (labelA) labelA.textContent = branchA
  if (labelB) labelB.textContent = branchB
}

/**
 * Generates a flexible branch comparison chart for Omzet, TC, or APC.
 */
export function generatePremiumBranchComparisonChart(
  periodData: SalesSummary[],
  branchA: string,
  branchB: string,
  canvasId: string,
  metric: 'totalOmzet' | 'totalTransactions' | 'apc',
  config?: { alsoStore?: AlsoStoreFn } // <--- ADDED
) {
  const daysInMonth = new Date(periodData[0]?.date.getFullYear(), periodData[0]?.date.getMonth() + 1, 0).getDate()
  const labels = Array.from({ length: daysInMonth }, (_, i) => String(i + 1))

  const getDailyData = (branchName: string) => {
    const dailyMap = new Map<string, number>()
    periodData.forEach((s) => {
      if (s.branches.includes(branchName)) {
        const day = String(s.date.getDate())
        dailyMap.set(day, (dailyMap.get(day) || 0) + s[metric])
      }
    })
    return labels.map((day) => dailyMap.get(day) || null)
  }

  const dataA = getDailyData(branchA)
  const dataB = getDailyData(branchB)

  // --- [NEW] AI Data Storage ---
  const alsoStore = createMaybeAlsoStoreFn(config?.alsoStore)
  const aiData: any = { [branchA]: {}, [branchB]: {} }
  
  const formatValue = metric === 'totalTransactions' ? formatNumberUtil : formatCurrencyUtil
  
  labels.forEach((label, i) => {
    if (dataA[i] !== null) aiData[branchA][label] = formatValue(dataA[i] as number)
    if (dataB[i] !== null) aiData[branchB][label] = formatValue(dataB[i] as number)
  })
  
  alsoStore(aiData, (v) => ({ [`${metric}_comparison_trend`]: v }))
  // --- [END NEW] ---

  const chartType = metric === 'totalTransactions' || metric === 'apc' ? 'bar' : 'line'
  const datasets: any[] = []

  if (metric === 'apc') {
    datasets.push({ type: 'line', label: `APC ${branchA}`, data: dataA, borderColor: '#A5B4FC', yAxisID: 'y-apc', tension: 0.1, spanGaps: true })
    datasets.push({ type: 'line', label: `APC ${branchB}`, data: dataB, borderColor: '#4F46E5', yAxisID: 'y-apc', tension: 0.1, spanGaps: true })
    datasets.push({ type: 'bar', label: `TC ${branchA}`, data: dataA, backgroundColor: '#A5B4FC', yAxisID: 'y-tc' })
    datasets.push({ type: 'bar', label: `TC ${branchB}`, data: dataB, backgroundColor: '#4F46E5', yAxisID: 'y-tc' })
  } else {
    datasets.push(
      { label: branchA, data: dataA, borderColor: '#A5B4FC', tension: 0.1, spanGaps: true },
      { label: branchB, data: dataB, borderColor: '#4F46E5', tension: 0.1, spanGaps: true },
    )
  }

  const options: any = metric === 'apc'
    ? {
        scales: {
          'y-tc': { type: 'linear', position: 'left', title: { display: true, text: 'Total Check (TC)' } },
          'y-apc': { type: 'linear', position: 'right', title: { display: true, text: 'Average Check (APC)' }, grid: { drawOnChartArea: false } },
        },
      }
    : { scales: { y: { beginAtZero: true, ticks: { callback: (n: any) => shortenCurrency(n) } } } }

  createChart(canvasId, 'line', { labels, datasets }, mergeChartOptions(
    options,
    chartTooltip({ label: (ctx: any) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}` }),
  ))
}
// --- END MODIFICATION (JOB_14) ---

/**
 * Generates a horizontal bar chart showing the top 5 menu items by revenue.
 */
export function generateTop5MenuChart(summaries: SalesSummary[], canvasId: string) {
  const menuRevenues = new Map<string, number>()

  summaries.forEach((summary) => {
    if (summary.menuItemRevenues) {
      for (const category in summary.menuItemRevenues) {
        for (const itemName in summary.menuItemRevenues[category]) {
          const revenue = summary.menuItemRevenues[category][itemName]
          menuRevenues.set(itemName, (menuRevenues.get(itemName) || 0) + revenue)
        }
      }
    }
  })

  // Sort by revenue (descending) and take top 5
  const sortedTop5 = Array.from(menuRevenues.entries())
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)

  createChart(canvasId, 'bar', {
    labels: sortedTop5.map(([name]) => name),
    datasets: [{
      label: 'Total Revenue',
      data: sortedTop5.map(([, revenue]) => revenue),
      backgroundColor: '#10B981', // Emerald Green for distinction
      borderRadius: 4,
    }],
  }, mergeChartOptions(
    { 
      indexAxis: 'y', // Horizontal orientation
      scales: { 
        x: { 
          ticks: { callback: (n: any) => shortenCurrency(n) } 
        },
        y: {
          grid: { display: false } 
        }
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: (context: any) => {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              // FIX: For horizontal charts, read context.parsed.x instead of y
              if (context.parsed.x !== null) {
                label += formatCurrency(context.parsed.x);
              }
              return label;
            }
          }
        }
      }
    }
  ))
}